import { Component, Inject, Input, OnChanges, SimpleChanges } from "@angular/core";
import { SequenceTriggerStep } from "@app2/clients/types/alert-typedefs";
import { SavedQueryService } from "@app2/search/saved-query.service";
import { SavedQuery } from "@app2/type-defs/search/search-types";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import { PRODUCT_DOMAIN } from "@app2/alert/alert-inbox/injection-tokens";
import { Domain } from "@app2/alert/constants";

export interface SequenceTriggerStepDetails {
    details: SequenceTriggerStep;
    visible: boolean;
}

@UntilDestroy()
@Component({
    selector: "sequence-trigger-steps-info",
    template: `
        <div *ngFor="let step of modifiedSteps; index as i; last as isLast"
             class="sequence-trigger-step"
             [attr.test-id]="'trigger-step-' + i">
            <ng-container *ngIf="productDomain === 'FRAUD'; else notFraudView">
                <div>
                    <ds-button type="blue-link"
                               (clicked)="step.visible = !step.visible"
                               test-id="step-query-button">
                        {{ (!step.visible ? "SHOW_STEP_QUERY" : "HIDE_STEP_QUERY") | localize:(i+1) }}
                        <i class="far" [ngClass]="{'fa-chevron-up': step.visible, 'fa-chevron-down': !step.visible}"></i>
                    </ds-button>
                    <saved-query-summary *ngIf="step.visible" [savedQueryId]="step.details.savedQueryId"></saved-query-summary>
                </div>
                <div *ngIf="step.visible && step.details.inputFields.length > 0" test-id="input-container">
                    <span class="matched-values-label">{{ "INPUT_FIELDS" | localize }}</span>
                    <span *ngFor="let input of step.details.inputFields; first as isFirst"
                          class="field-text"
                          [attr.test-id]="'match-input-' + input">
                        {{ isFirst ? '' : ',' }}
                        {{ input }}
                    </span>
                </div>
                <div *ngIf="step.visible && step.details.outputFields.length > 0" test-id="output-container">
                    <span class="matched-values-label">{{ "OUTPUT_FIELDS" | localize }}</span>
                    <span *ngFor="let output of step.details.outputFields; first as isFirst"
                          class="field-text"
                          [attr.test-id]="'match-output-' + output">
                        {{ isFirst ? '' : ',' }}
                        {{ output }}
                    </span>
                </div>
            </ng-container>
            <ng-template #notFraudView>
                <div>
                    <span class="sequence-trigger-step-label"> {{ 'SEARCH_QUERY_STEP_NUMBER' | localize: i + 1 }} </span>
                    <search-link [queryString]="savedQueries[steps[i].savedQueryId]?.query || ''"
                                 [defaultText]="true">
                    </search-link>
                    <div class="match-count-container">
                        <span>{{ "SEARCH_QUERY_MATCH_COUNT" | localize}}: </span>
                        <span class="sequence-trigger-threshold">
                        {{ step.details.targetCount }}
                    </span>
                        <i class="far fa-arrow-up"></i>
                    </div>
                </div>
            </ng-template>
        </div>
       `,
    styles: [`
        @import "/src/styles/colors";
        @import "/src/styles/settings";

        .sequence-trigger-step {
            margin-bottom: 8px;
        }

        .matched-values-label {
            color: $sub-header;
            font-size: 0.75rem;
        }
    `],
})
export class SequenceTriggerStepsInfoComponent implements OnChanges {
    @Input() steps: SequenceTriggerStep[];
    modifiedSteps: SequenceTriggerStepDetails[];
    matchedFields: string[];

    savedQueries: Record<uuid, SavedQuery> = {};

    constructor(@Inject(PRODUCT_DOMAIN) public productDomain: Domain,
                private savedQueryService: SavedQueryService) {
        this.savedQueryService.getSavedQueries$()
            .pipe(untilDestroyed(this))
            .subscribe(savedQueries => this.savedQueries = savedQueries);
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes.steps && this.steps) {
            this.modifiedSteps = this.steps.map(step => ({ details: step, visible: false }));
        }
    }
}
